import { Localized } from "@fluent/react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { Alert, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes"
import { SaveButton } from "../../../../app/components/SaveButton"
import { StructureTreeSelect } from "../../../structure/StructureTreeSelect"
import { DocumentCreationModes, DocumentLanguages, DocumentTypes, UrsNumberingMode } from "../../documentTypes"
import { DocumentTypeSelect } from "../../controls/DocumentTypeSelect"
import { DocumentLanguageSelect } from "../../controls/DocumentLanguageSelect"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { RequiredError } from "../../../../app/components/RequiredError"
import { DocumentHeader } from "../../documentsApi"
import { CancelButton } from "../../../../app/components/CancelButton"
import { createClientDocumentTemplate } from "../documentTemplateApi"
import { selectStructureNodeById } from "../../../device/structureSlice"
import { Button, InputAdornment } from "@mui/material";
import { LoadURSTemplates } from "../../../../app/AppDataLoader"
import { selectAllUrsTemplates, UrsTemplate } from "../../../urstemplates/ursTemplatesSlice"
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Relationship, selectStructureRelationship } from "../../../device/structureRelationshipSlice"
import { StructureName } from "./StructureTreeView"

export interface CreateClientDocumentTemplateDialogProps {
  defaultStructureId: AppId | null;
  processId: AppId | null
  defaultType: DocumentTypes
  onSucces: (document: DocumentHeader) => void
  onCancel: () => void
}

export const CreateClientDocumentTemplateDialog = (props: CreateClientDocumentTemplateDialogProps) => {
  const dispatch = useAppDispatch()

  const [documentNumber, setDocumentNumber] = useState("")
  const [title, setTitle] = useState("")
  const [language, setLanguage] = useState<DocumentLanguages>("EN")
  const [docType, setDocType] = useState<DocumentTypes>(props.defaultType)
  const [structureId, setStructureId] = useState<AppId | "">(props.defaultStructureId ?? "")
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState("")
  const structure = useAppSelector(state =>
    structureId ? selectStructureNodeById(state, structureId as AppId) : null
  );

  const [numberDirty, setNumberDirty] = useState(false)
  const [titleDirty, setTitleDirty] = useState(false)
  const isUrs = docType === "URS";

  const [createFrom, setCreateFrom] = useState<DocumentCreationModes>("EMPTY");
  const [numberingMode, setNumberingMode] = useState<UrsNumberingMode>("UNIQUE_REQ");

  const [templateId, setTemplateId] = useState<number>(0);

  const [validationErrors, setValidationErrors] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<UrsTemplate | undefined>(undefined);
  const [templateDialogVisible, setTemplateDialogVisible] = useState<boolean>(false);
  const [showForType, setShowForType] = useState(true);
  const deviceType = useAppSelector(state => selectStructureNodeById(state, structureId ?? ""))
  const relationship = useAppSelector(selectStructureRelationship)
  const allURSTemplates = useAppSelector(selectAllUrsTemplates)

  const URSTemplates = useMemo(() => {
    if (showForType) {
      return allURSTemplates.filter(x => x.deviceTypeId == (deviceType?.id ?? ""))
    } else if (deviceType) {
      const rel = relationship.entities[deviceType.parentStructureId ?? ""] as Relationship
      const ids = rel.succs.filter(x => x.leaf === true).map(x => x.structureId)
      return allURSTemplates.filter(t => ids.some(structureId => structureId === t.deviceTypeId))
    } else {
      return allURSTemplates
    }
  }, [allURSTemplates, showForType, deviceType, relationship])


  const handleShowForTypeChange = (event) => {
    setShowForType(event.target.checked);
  };

  const handleChooseTemplateClick = useCallback((template: UrsTemplate) => {
    setSelectedTemplate(template)
    setTemplateDialogVisible(false)
  }, [])

  const handleCancelUrsTemplateClick = useCallback(() => {
    setTemplateDialogVisible(false)
  }, [])

  useEffect(() => {
    if (!numberDirty) {
      setDocumentNumber(
        buildDefaultNumber(String(docType), structure?.code, String(language))
      );
    }
    if (!titleDirty) {
      setTitle(buildDefaultTitle(String(docType), String(language), structure ?? undefined));
    }
  }, [docType, language, structureId, structure?.name, structure?.code]);

  const handleStructureSelected = useCallback((structureId: AppId) => {
    if (structureId) setStructureId(structureId);
  }, []);

  const handleTitleChange: AppTextFieldHandler = useCallback(e => {
    setTitle(e.target.value);
    setTitleDirty(true);
  }, [])

  const handleDocumentNumberChange: AppTextFieldHandler = useCallback(e => {
    setDocumentNumber(e.target.value);
    setNumberDirty(true);
  }, [])

  const handleLanguageChange = useCallback((language: DocumentLanguages | "") => {
    if (language) setLanguage(language);
  }, []);

  const handleDocTypeChange = useCallback((docType: DocumentTypes) => {
    if (docType) setDocType(docType);
  }, []);


  const buildDefaultNumber = (
    type?: string,
    structCode?: string,
    lang?: string,
    date: Date = new Date()
  ) => {
    if (!type || !lang) return "";

    const year = date.getFullYear();
    const t = String(type).toUpperCase();
    const sc = (structCode ? String(structCode) : "GEN")
      .toUpperCase()
      .replace(/\s+/g, "-");
    const l = String(lang).toUpperCase();

    return `${t}-${sc}-${year}-${l}`;
  };

  const buildDefaultTitle = (
    type?: string,
    langCode?: string,
    st?: { name?: string; name_EN?: string; code?: string }
  ) => {
    if (!type || !langCode) return "";

    const lc = langCode.toUpperCase();
    const phrase = lc === "EN" ? "Template for" : "Szablon dla";
    const langWord = lc === "EN" ? "language" : "język";
    const deviceName =
      lc === "EN"
        ? (st?.name_EN ?? st?.name ?? "")
        : (st?.name ?? st?.name_EN ?? "");

    const devicePart = deviceName
      ? `"${deviceName}"${st?.code ? ` (${st.code})` : ""}`
      : "";

    const langPart = `${langWord} (${lc})`;

    return devicePart
      ? `${type} - ${phrase} - ${devicePart} - ${langPart}`
      : `${type} - ${langPart}`;
  };



  const handleSaveClick = async () => {
    // if (isUrs && createFrom === "TEMPLATE" && !selectedTemplate) {
    //   setServerError("Wybierz szablon URS");
    //   return;
    // }


    if (documentNumber && title && structureId && docType && language) {
      setSaving(true)
      try {


        const selectedTemplateId =
          createFrom === "TEMPLATE" ? (selectedTemplate?.id ?? "") : "";

        const result = await dispatch(createClientDocumentTemplate({
          title,
          docDesignation: "",
          documentNumber,
          structureId: structureId,
          documentType: docType,
          language,
          templateId: selectedTemplateId,
          copyDocumentId: 0,
          creationMode: createFrom,
          numberingMode: isUrs ? numberingMode : undefined
        }))

        if (createClientDocumentTemplate.fulfilled.match(result)) {
          const template = result.payload as DocumentHeader;
          dispatch(showSuccess("saved"))
          props.onSucces(template)
        } else {
          let errorMsg = "error"
          if (result.payload?.kind === 'http') {
            const problem = result.payload.problem
            if (problem) errorMsg = problem.title
          }
          setServerError(errorMsg)
        }
      } catch {
        dispatch(showError("error"))
      } finally {
        setSaving(false)
      }
    }
  }

  return (
    <Dialog open={true} fullWidth maxWidth='md'>
      <DialogTitle>
        <Localized id='document-create-new-template'>_New template</Localized></DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ padding: 2 }}>
          <Grid item xs={12}>
            <StructureTreeSelect
              defaultStructureId={structureId}
              onStructureSelected={handleStructureSelected}
            />
          </Grid>

          <Grid item xs={4}>
            <DocumentTypeSelect defaultValue={docType} required onChange={handleDocTypeChange} />
          </Grid>
          <Grid item xs={4}>
            <DocumentLanguageSelect defaultValue={language} required onChange={handleLanguageChange} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              helperText={documentNumber === '' && <RequiredError />}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={documentNumber}
              error={documentNumber === ''}
              inputProps={{ maxLength: 50 }}
              onChange={handleDocumentNumberChange}
              label={<Localized id="document-number"><span>_Numer dokumentu</span></Localized>}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              helperText={title === '' && <RequiredError />}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={title}
              error={title === ''}
              onChange={handleTitleChange}
              label={<Localized id="document-title"><span>_Tytuł</span></Localized>}
            />
          </Grid>
          {/* {isUrs && createFrom === "EMPTY" && (
            <Grid item xs={12}>
              <TextField
                value={numberingMode}
                sx={{ width: "50%" }}
                select
                label="Sposób numerowania"
                onChange={(e) => setNumberingMode(e.target.value as UrsNumberingMode)}
              >
                <MenuItem value={"UNIQUE_REQ"}>Unikalne</MenuItem>
                <MenuItem value={"DECIMAL_HIERARCHICAL"}>Hierarchiczne</MenuItem>
              </TextField>
            </Grid>
          )} */}
          {/* {isUrs && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        color={createFrom === "EMPTY" ? "secondary" : "primary"}
                        onClick={() => { setCreateFrom("EMPTY"); setTemplateId(0); }}
                      >
                        Pusty
                      </Button>
                      <Button
                        size="small"
                        color={createFrom === "TEMPLATE" ? "secondary" : "primary"}
                        onClick={() => setCreateFrom("TEMPLATE")}
                      >
                        Szablon
                      </Button>
                    </InputAdornment>
                  ),
                }}
                label="Jak wygenerować URS?"
              />
            </Grid>
          )}
          {isUrs && createFrom === "TEMPLATE" && (
            <Grid item xs={12}>
              <LoadURSTemplates component={<Skeleton />}>
                <TextField
                  label={<Localized id="urs-template"><span>_Szablon URS</span></Localized>}
                  value={selectedTemplate?.ursNo}
                  fullWidth
                  required
                  InputLabelProps={{
                    shrink: true
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: <InputAdornment position="end"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <IconButton
                        onClick={() => setTemplateDialogVisible(true)}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  }}
                >
                </TextField>
              </LoadURSTemplates>
            </Grid>
          )} */}
        </Grid>

        {serverError && (
          <Alert sx={{ mt: 1 }} onClose={() => setServerError("")} severity="error">
            <Localized id={serverError}><span>_Server error</span></Localized>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <SaveButton loading={saving} onClick={handleSaveClick} />
        <CancelButton loading={saving} onClick={props.onCancel} />
      </DialogActions>
      <Dialog open={templateDialogVisible} fullWidth maxWidth="md">
        <DialogTitle>
          <Localized id="urs-template">
            <span>_Szablon URS</span>
          </Localized>
        </DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={showForType}
                onChange={handleShowForTypeChange}
                color="primary"
              />
            }
            label={<Localized id="urs-create-show-for-device-type">
              <span>_Pokaż tylko zgodne z typem urządzenia</span>
            </Localized>
            }
          />
          <TableContainer
            component={Paper}
            sx={{ display: 'flex', height: 'calc(100vh - 440px)' }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      <Localized id="urs-template-code">
                        <span>_Kod</span>
                      </Localized>
                    </Typography>
                  </TableCell>
                  <TableCell >
                    <Typography variant="subtitle2" color="textSecondary">
                      <Localized id="urs-template-description">
                        <span>_Opis</span>
                      </Localized>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 300, width: 300, maxWidth: 300 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      <Localized id="urs-device-type">
                        <span>_Nazwa typu rządzenia</span>
                      </Localized>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      <Localized id="system-item">
                        <span>_Systemowy</span>
                      </Localized>
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {URSTemplates.map(row => {
                  return <TableRow key={row.id}>
                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}><Typography component="span">{row.ursNo}</Typography></TableCell>
                    <TableCell ><Typography component="span">{row.docDesignation}</Typography></TableCell>
                    <TableCell sx={{ minWidth: 300, width: 300, maxWidth: 300 }}><StructureName structureId={row.deviceTypeId} /></TableCell>
                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                      <Checkbox defaultChecked={row.isSystem} disabled />
                    </TableCell>

                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                      <Button color="primary" variant="outlined" onClick={() => handleChooseTemplateClick(row)} >
                        <Localized id="select">
                          <span>_Wybierz</span>
                        </Localized>
                      </Button>
                    </TableCell>
                  </TableRow>
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUrsTemplateClick}>
            <Localized id="cancel">
              <span>_Anuluj</span>
            </Localized>
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

  );
}
